// Generated by GitHub Copilot
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { State, useComponentState } from '#core/state/State.ts'
import CompanyInvitationsApi from '#core/api/auth/CompanyInvitationsApi.ts'
import type CompanyInvitationIssue from '#core/entities/auth/companies/CompanyInvitationIssue.ts'
import type RoleDetail from '#core/entities/auth/roles/RoleDetail.ts'
import { showToast } from '#core/notifications/toast.ts'
import { CardModal } from '#components/primitives/components/card-modal.tsx'
import { FormField } from '#components/primitives/forms/form-field.tsx'
import { FormFooterActions } from '#components/primitives/components/form-footer-actions.tsx'

const invitationsApi = new CompanyInvitationsApi()

interface InviteUserModalProps {
  isOpen: boolean
  roles: RoleDetail[]
  onClose: () => void
  onSaved: (issue: CompanyInvitationIssue) => void
}

/** Inline state for the invite user modal. */
class InviteUserState extends State<InviteUserState> {
  email = ''
  roleId = ''
  saving = false

  reset(): void {
    this.reactTo.email = ''
    this.reactTo.roleId = ''
    this.reactTo.saving = false
  }

  async submit(onSaved: (issue: CompanyInvitationIssue) => void, onClose: () => void): Promise<void> {
    this.reactTo.saving = true
    try {
      const result = await invitationsApi.createInvitation(
        this.email,
        this.roleId || undefined,
      )
      if (result) {
        onSaved(result)
        onClose()
      }
    } catch {
      showToast(i18n.t('usersTeams.toast.sendInvitationError', { ns: 'settings' }), 'danger')
    } finally {
      this.reactTo.saving = false
    }
  }
}

/** Modal for creating a new company invitation. */
export function InviteUserModal({ isOpen, roles, onClose, onSaved }: InviteUserModalProps) {
  const { t } = useTranslation('settings')
  const state = useComponentState(InviteUserState)

  useEffect(() => {
    if (isOpen) state.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const footer = (
    <FormFooterActions
      onCancel={onClose}
      onSubmit={() => void state.submit(onSaved, onClose)}
      submitting={state.saving}
      submitDisabled={!state.email || !state.roleId}
      submitLabel={t('usersTeams.inviteModal.send')}
      submittingLabel={t('usersTeams.inviteModal.send')}
    />
  )

  return (
    <CardModal isOpen={isOpen} title={t('shared.fields.inviteUser')} footer={footer} onClose={onClose}>
      <FormField label={t('shared.fields.emailAddress')} htmlFor="invite-user-email">
        <input
          id="invite-user-email"
          type="email"
          className="form-control"
          value={state.email}
          onChange={e => { state.email = e.target.value }}
        />
      </FormField>

      <FormField label={t('shared.fields.role')} htmlFor="invite-user-role" className="mb-0" required>
        <select
          id="invite-user-role"
          className="form-select"
          value={state.roleId}
          onChange={e => { state.roleId = e.target.value }}
        >
          <option value="">&mdash;</option>
          {roles.map(r => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
      </FormField>
    </CardModal>
  )
}
